import React, { useState, useEffect } from "react";
import {
  Box,
  Grid,
  Card,
  CardMedia,
  CardContent,
  Typography,
  Button,
  Modal,
  IconButton,
  Pagination,
  CircularProgress,
  Alert,
  Tooltip,
  Stack,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import CloseIcon from "@mui/icons-material/Close";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import VideoFileIcon from "@mui/icons-material/VideoFile";
import FileCopyIcon from "@mui/icons-material/FileCopy";
import DeleteIcon from "@mui/icons-material/Delete";
import AudiotrackOutlinedIcon from "@mui/icons-material/AudiotrackOutlined";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import PlayCircleFilledIcon from "@mui/icons-material/PlayCircleFilled";
import { toast } from "react-toastify";
import axios from "axios";

import { API_ENDPOINT } from "../../utils/constants";
import {
  getErrorResponse,
  getHeader,
  getMultipartHeader,
  toastError,
} from "../../utils/util";

import { GlassButton } from "../../themes/UiElements";
import UploadAudio from "./UploadAudio";
import PageTitle from "../../widgets/PageTitle";
import CustomCircularP from "../../widgets/CustomCircularP";
import { useDialog } from "../../context/DialogContext";

const VisuallyHiddenInput = styled("input")({
  clip: "rect(0 0 0 0)",
  clipPath: "inset(50%)",
  height: 1,
  overflow: "hidden",
  position: "absolute",
  bottom: 0,
  left: 0,
  whiteSpace: "nowrap",
  width: 1,
});

const StyledCard = styled(Card)(({ theme }) => ({
  transition: theme.transitions.create(["box-shadow"]),
  "&:hover": {
    boxShadow: theme.shadows[4],
  },
}));

const MediaLibrary = () => {
  const { showDialog } = useDialog();
  const [mediaFiles, setMediaFiles] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState(null);
  const [openModal, setOpenModal] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadError, setUploadError] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [copiedId, setCopiedId] = useState(null);

  const limit = 20;

  // Fetch uploaded media files
  const fetchMediaFiles = async (pageNum) => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(
        `${API_ENDPOINT}/upload/get-uploaded-files`,
        {
          ...getHeader(),
          params: { page: pageNum, limit },
        }
      );
      setMediaFiles(response.data.data);
      setTotalPages(response.data.totalPages);
      setTotalItems(response.data.totalItems);
      setPage(response.data.currentPage);
    } catch (err) {
      setError("Failed to fetch media files. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMediaFiles(page);
  }, [page]);

  // Handle file selection
  const handleFileChange = (event) => {
    setSelectedFile(event.target.files[0]);
    setUploadError(null);
  };

  // Handle file upload
  const handleUpload = async () => {
    if (!selectedFile) {
      setUploadError("Please select a file to upload.");
      return;
    }

    setUploading(true);
    setUploadError(null);

    const formData = new FormData();
    formData.append("file", selectedFile);

    try {
      await axios.post(
        `${API_ENDPOINT}/upload/media/upload-asset`,
        formData,
        getMultipartHeader()
      );
      setOpenModal(false);
      setSelectedFile(null);
      fetchMediaFiles(page);
      toast.success("Media uploaded successfully");
    } catch (err) {
      setUploadError(getErrorResponse(err));
    } finally {
      setUploading(false);
    }
  };

  // Handle delete
  const handleDelete = async (id) => {
    showDialog("Please confirm", "Are you sure you want to delete this?", {
      type: "delete",
      onConfirm: async () => {
        try {
          setDeleting(true);
          const response = await axios.delete(
            `${API_ENDPOINT}/upload/delete-media/${id}`,
            getHeader()
          );
          toast.success(response.data.message);
          fetchMediaFiles();
        } catch (error) {
          toastError(error);
        } finally {
          setDeleting(false);
        }
      },
    });
  };

  // Handle copy header_handle
  const handleCopy = (headerHandle, id) => {
    navigator.clipboard.writeText(headerHandle).then(() => {
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 2000);
    });
  };

  const handlePreviewClick = (url) => {
    window.open(url, "_blank", "noopener,noreferrer");
  };

  return (
    <Box sx={{ p: 3 }}>
      <CustomCircularP show={deleting} />
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          justifyContent: "space-between",
          alignItems: { xs: "flex-start", sm: "center" },
          gap: 2,
          mb: 3,
        }}
      >
        <PageTitle>Media Library</PageTitle>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1}
          sx={{
            width: { xs: "100%", sm: "auto" },
            mt: { xs: 1, sm: 0 },
          }}
        >
          <UploadAudio
            apiEndpoint={`${API_ENDPOINT}/upload/media/upload-audio`}
            onSuccess={(data) => {
              toast.success(data.message);
            }}
            onError={(error) => {
              toastError(error);
            }}
          />
          <GlassButton
            variant="contained"
            startIcon={<CloudUploadIcon />}
            onClick={() => setOpenModal(true)}
            sx={{ width: { xs: "100%", sm: "auto" } }}
          >
            Upload Media
          </GlassButton>
        </Stack>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", my: 4 }}>
          <CircularProgress />
        </Box>
      ) : mediaFiles.length === 0 ? (
        <Typography>No media files found.</Typography>
      ) : (
        <>
          <Grid container spacing={{ xs: 1, sm: 2 }}>
            {mediaFiles.map((media) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={media._id}>
                <StyledCard>
                  <Box
                    sx={{ cursor: "pointer" }}
                    onClick={() => handlePreviewClick(media.preview_url)}
                  >
                    {["image/jpeg", "image/jpg", "image/png"].includes(
                      media.mime_type
                    ) ? (
                      <CardMedia
                        component="img"
                        height="140"
                        image={media.preview_url}
                        alt={media.file_name}
                        sx={{ objectFit: "cover" }}
                      />
                    ) : media.mime_type === "video/mp4" ? (
                      <Box sx={{ position: "relative", height: 140 }}>
                        <CardMedia
                          component="video"
                          height="140"
                          src={media.preview_url}
                          sx={{ objectFit: "cover", width: "100%" }}
                        />
                        <Box
                          sx={{
                            position: "absolute",
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            bgcolor: "rgba(0, 0, 0, 0.4)",
                          }}
                        >
                          <PlayArrowIcon
                            sx={{ color: "white", fontSize: 60 }}
                          />
                        </Box>
                      </Box>
                    ) : media.mime_type === "application/pdf" ? (
                      <Box
                        sx={{
                          height: 140,
                          p: 1,
                          bgcolor: "grey.100",
                          position: "relative",
                        }}
                      >
                        <iframe
                          src={media.preview_url}
                          style={{ width: "100%", height: "100%" }}
                          frameBorder="0"
                        />
                        <Box
                          sx={{
                            position: "absolute",
                            top: "50%",
                            left: "50%",
                            transform: "translate(-50%, -50%)",
                            bgcolor: "rgba(0, 0, 0, 0.5)",
                            borderRadius: "50%",
                            p: 1,
                          }}
                        >
                          <PictureAsPdfIcon
                            sx={{ color: "white", fontSize: 40 }}
                          />
                        </Box>
                      </Box>
                    ) : media.mime_type === "audio/webm" ? (
                      <Box
                        sx={{
                          height: 140,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          bgcolor: "grey.200",
                          position: "relative",
                          cursor: "pointer",
                        }}
                      >
                        <PlayCircleFilledIcon
                          sx={{ fontSize: 60, color: "primary.main" }}
                        />
                      </Box>
                    ) : (
                      <Box
                        sx={{
                          height: 140,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          bgcolor: "grey.200",
                        }}
                      >
                        <Typography variant="body2" color="text.secondary">
                          {media.mime_type}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                  <CardContent sx={{ p: 2 }}>
                    <Typography variant="body2" noWrap sx={{ mb: 1 }}>
                      {media.file_name}
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: "block", mb: 0.5 }}
                    >
                      Type: {media.mime_type}
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: "block", mb: 0.5 }}
                    >
                      Media ID: {media?.mediaId || ""}
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: "block", mb: 1 }}
                    >
                      Uploaded: {new Date(media.createdOn).toLocaleDateString()}
                    </Typography>

                    {/* Actions */}
                    <Box sx={{ display: "flex", gap: 1 }}>
                      <Tooltip
                        title={
                          copiedId === media._id
                            ? "Copied!"
                            : "Copy header_handle"
                        }
                      >
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleCopy(media.header_handle, media._id);
                          }}
                        >
                          <FileCopyIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete media">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDelete(media._id);
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </CardContent>
                </StyledCard>
              </Grid>
            ))}
          </Grid>

          <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
            <Pagination
              count={totalPages}
              page={page}
              onChange={(e, value) => setPage(value)}
              color="primary"
            />
          </Box>
          <Typography
            variant="caption"
            sx={{ mt: 1, display: "block", textAlign: "center" }}
          >
            Total Items: {totalItems}
          </Typography>
        </>
      )}

      {/* Upload Modal */}
      <Modal open={openModal} onClose={() => setOpenModal(false)}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 400,
            bgcolor: "background.paper",
            boxShadow: 24,
            p: 4,
            borderRadius: 2,
          }}
        >
          <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
            <Typography variant="h6">Upload Media</Typography>
            <IconButton onClick={() => setOpenModal(false)}>
              <CloseIcon />
            </IconButton>
          </Box>
          {uploadError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {uploadError}
            </Alert>
          )}
          <Button
            component="label"
            variant="outlined"
            startIcon={<CloudUploadIcon />}
            sx={{ mb: 2 }}
          >
            Select File
            <VisuallyHiddenInput
              type="file"
              accept="image/*,video/*,application/pdf,audio/*"
              onChange={handleFileChange}
            />
          </Button>
          {selectedFile && (
            <Typography variant="body2" sx={{ mb: 2 }}>
              Selected: {selectedFile.name}
            </Typography>
          )}
          <Button
            variant="contained"
            onClick={handleUpload}
            disabled={uploading || !selectedFile}
            fullWidth
          >
            {uploading ? <CircularProgress size={24} /> : "Upload"}
          </Button>
        </Box>
      </Modal>
    </Box>
  );
};

export default MediaLibrary;
